import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { useI18n } from '../lib/i18n';
import { DateTile } from './ui';

/*
 * The pieces of an appointment's card, shared by the review, the done screen, the manage page,
 * the waitlist and the offer page.
 */

/**
 * The top of an appointment's card: its day as a little calendar page, then two lines. A
 * description list of its own, its terms read out but not shown (the layout says which is which).
 */
export function AppointmentHead({
  iso,
  timeZone,
  first,
  second,
}: {
  iso: string;
  timeZone: string;
  first: { label: string; value: ReactNode };
  second: { label: string; value: ReactNode };
}) {
  const { locale } = useI18n();
  return (
    <div className="flex items-center gap-4 bg-accent/60 p-4 sm:p-5">
      <DateTile iso={iso} timeZone={timeZone} locale={locale} />
      <dl className="min-w-0 space-y-0.5">
        <div>
          <dt className="sr-only">{first.label}</dt>
          <dd className="text-lg font-semibold leading-snug text-heading">{first.value}</dd>
        </div>
        <div>
          <dt className="sr-only">{second.label}</dt>
          <dd className="text-base text-foreground">{second.value}</dd>
        </div>
      </dl>
    </div>
  );
}

/** One fact: its icon and name above, the fact below. Inside a `<dl>`. */
export function Detail({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
}) {
  // One `div` per term, holding only its `dt` and `dd` — the only nesting a
  // `<dl>` allows, so the icon lives inside the term.
  return (
    <div className="py-3.5">
      <dt className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        {label}
      </dt>
      <dd className="mt-0.5 pl-6 text-base font-medium text-heading">{value}</dd>
    </div>
  );
}
